/* eslint-disable @typescript-eslint/no-unused-vars */
import {
  Asset,
  BASE_FEE,
  FeeBumpTransaction,
  Operation,
  rpc,
  Transaction,
  TransactionBuilder,
} from "@stellar/stellar-sdk";
import { BigNumber } from "bignumber.js";
import { Buffer } from "buffer";

import {
  Address,
  EstimateERC20TransferParams,
  EstimateNftTransferParams,
  FeesEstimation,
  Hash,
  Hex,
  KeybanAccount,
  TransferERC20Params,
  TransferNftParams,
} from "~/index";
import { RpcClient } from "~/rpc";
import { StellarSigner } from "~/stellar/signer";

const TX_VALIDITY_DURATION = 25; // Slightly below the default polling timeout of 30 seconds
const STELLAR_DECIMALS = 7;
BigNumber.config({
  DECIMAL_PLACES: STELLAR_DECIMALS,
});

export class StellarAccount extends KeybanAccount {
  address: string;
  publicKey: string;
  #signer: StellarSigner;
  #rpcProvider: rpc.Server;

  constructor(
    rpcClient: RpcClient,
    rpcProvider: rpc.Server,
    signer: StellarSigner,
    publicKey: string,
  ) {
    super(rpcClient);

    this.#rpcProvider = rpcProvider;
    this.#signer = signer;

    this.address = publicKey; // stellar addresses aren't hexa https://stellar.github.io/js-stellar-sdk/Address.html#Address
    this.publicKey = publicKey; // account.accountId == publicKey == address
  }

  async signMessage(message: string): Promise<Hex | string[]> {
    const hash = await crypto.subtle.digest(
      "SHA-256",
      new TextEncoder().encode(message),
    );
    const signedMessage = await this.#signer.sign(Buffer.from(hash));
    return signedMessage.toString("base64");
  }

  async transfer(to: Address, value: bigint): Promise<string> {
    const [account, networkDetails] = await Promise.all([
      this.#rpcProvider.getAccount(this.address),
      this.#rpcProvider.getNetwork(),
    ]);
    const paymentTx = new TransactionBuilder(account, {
      fee: BASE_FEE,
      networkPassphrase: networkDetails.passphrase,
    })
      .addOperation(
        Operation.payment({
          destination: to,
          asset: Asset.native(),
          amount: BigNumber(value)
            .dividedBy(10 ** STELLAR_DECIMALS)
            .toString(),
        }),
      )
      .setTimeout(TX_VALIDITY_DURATION)
      .build();

    // sign the transaction
    const signature = await this.#signer.signDecorated(paymentTx.hash());
    paymentTx.addDecoratedSignature(signature);

    // submit the transaction to the network
    return this.#submitTransaction(paymentTx);
  }

  estimateTransfer(_to: Address): Promise<FeesEstimation> {
    throw new Error("estimateTransfer not implemented.");
  }

  transferERC20(_params: TransferERC20Params): Promise<Hash> {
    throw new Error("transferERC20 not implemented.");
  }

  estimateERC20Transfer(
    _params: EstimateERC20TransferParams,
  ): Promise<FeesEstimation> {
    throw new Error("estimateERC20Transfer not implemented.");
  }

  transferNft(_params: TransferNftParams): Promise<Hash> {
    throw new Error("transferNft not implemented.");
  }

  estimateNftTransfer(
    _params: EstimateNftTransferParams,
  ): Promise<FeesEstimation> {
    throw new Error("estimateNftTransfer not implemented.");
  }

  async #submitTransaction(
    tx: Transaction | FeeBumpTransaction,
  ): Promise<string> {
    return this.#rpcProvider
      .sendTransaction(tx)
      .then(async (response) => {
        if (response.status !== "PENDING") {
          throw response; // TODO handle this better
        }
        return this.#rpcProvider.pollTransaction(response.hash);
      })
      .then((finalResponse) => {
        switch (finalResponse.status) {
          case rpc.Api.GetTransactionStatus.FAILED:
          case rpc.Api.GetTransactionStatus.NOT_FOUND:
            throw finalResponse; // TODO handle this better
          case rpc.Api.GetTransactionStatus.SUCCESS:
            return finalResponse.txHash;
        }
      });
  }

  async loyaltySetup(): Promise<void> {
    try {
      const account = await this.#rpcProvider.getAccount(this.publicKey);
      if (account) return;
    } catch {
      console.log("Stellar Account not yet setup for Loyalty, Doing it now...");
    }
    // Init the account setup
    const rawTx = await this.rpcClient.call("loyalty", "initAccountSetup");
    // Decode the raw transaction
    const { passphrase: networkPassphrase } =
      await this.#rpcProvider.getNetwork();
    const setupTx = new Transaction(rawTx, networkPassphrase);
    // sign the transaction
    const signature = await this.#signer.signDecorated(setupTx.hash());
    setupTx.addDecoratedSignature(signature);
    // serialize and submit the transaction to the network
    await this.rpcClient.call("loyalty", "submitAccountSetup", setupTx.toXDR());
  }
}
